import React from "react";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  TableContainer,
  TablePagination,
  Skeleton,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Box,
  useTheme,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import InfoIcon from "@mui/icons-material/Info";
import { ContactDto } from "../../../types/crm/types/contact";

interface ContactsTableProps {
  contacts: ContactDto[];
  loading: boolean;
  error: string | null;
  selectedRow: number | null;
  page: number;
  rowsPerPage: number;
  onRowClick: (contactId: number) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ContactsTable: React.FC<ContactsTableProps> = ({
  contacts,
  loading,
  error,
  selectedRow,
  page,
  rowsPerPage,
  onRowClick,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <TableContainer sx={{ flex: 1 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", bgcolor: theme.palette.grey[100] }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: "bold", bgcolor: theme.palette.grey[100] }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: "bold", bgcolor: theme.palette.grey[100] }}>Dirección</TableCell>
              <TableCell sx={{ fontWeight: "bold", bgcolor: theme.palette.grey[100] }}>Teléfono</TableCell>
              <TableCell sx={{ fontWeight: "bold", bgcolor: theme.palette.grey[100] }}>Email</TableCell>
              <TableCell sx={{ fontWeight: "bold", bgcolor: theme.palette.grey[100] }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array(rowsPerPage).fill(0).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" /></TableCell>
                  <TableCell><Skeleton variant="text" /></TableCell>
                  <TableCell><Skeleton variant="text" /></TableCell>
                  <TableCell><Skeleton variant="text" /></TableCell>
                  <TableCell><Skeleton variant="text" /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Stack alignItems="center" spacing={1}>
                    <InfoIcon color="disabled" fontSize="large" />
                    <Typography color="textSecondary">
                      No se encontraron contactos
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              contacts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contact) => (
                  <TableRow
                    key={contact.id}
                    hover
                    onClick={() => onRowClick(contact.id)}
                    selected={selectedRow === contact.id}
                    sx={{
                      cursor: "pointer",
                      '&:last-child td': { borderBottom: 0 },
                      backgroundColor: selectedRow === contact.id 
                        ? theme.palette.action.selected 
                        : 'inherit',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">              
                        <Avatar 
                          src={contact.userProfileImage || "/avatar-placeholder.png"} 
                          sx={{ width: 45, height: 45 }}
                        />
                        <Box>
                          <Typography fontWeight="medium">
                            {contact.fullName || "Sin nombre"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={contact.contactType || "Sin tipo"}
                        size="small"
                        color={
                          contact.contactType === "Cliente" ? "primary" : 
                          contact.contactType === "Proveedor" ? "secondary" : "default"
                        }
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {contact.address || "Sin dirección"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {contact.phone || "Sin teléfono"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {contact.email || "Sin email"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Ver detalles">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowClick(contact.id);
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar email">
                          <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                            <MailOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Compartir">
                          <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={contacts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage="Contactos por página:"
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          '& .MuiTablePagination-toolbar': {
            padding: 2,
          }
        }}
      />
    </Paper>
  );
};

export default ContactsTable;